export type FamilyRoleName = 'owner' | 'co_manager' | 'contributor' | 'scanner' | 'viewer';

export type FamilyPermissionKey =
  | 'expenses.view' | 'expenses.add' | 'expenses.scan'
  | 'expenses.edit_others' | 'expenses.approve_pending'
  | 'budget.view' | 'budget.edit'
  | 'goals.view' | 'goals.contribute' | 'goals.edit'
  | 'insights.view'
  | 'income.view' | 'income.edit'
  | 'family.manage';

export const ALL_PERMISSIONS: FamilyPermissionKey[] = [
  'expenses.view', 'expenses.add', 'expenses.scan',
  'expenses.edit_others', 'expenses.approve_pending',
  'budget.view', 'budget.edit',
  'goals.view', 'goals.contribute', 'goals.edit',
  'insights.view', 'income.view', 'income.edit', 'family.manage',
];

/**
 * Mirrors the role presets inside the `has_family_permission` database
 * function. The database is the enforcement point; this table only drives UI.
 * Keep the two in sync.
 */
export const ROLE_PRESETS: Record<FamilyRoleName, FamilyPermissionKey[]> = {
  owner: [...ALL_PERMISSIONS],
  co_manager: [...ALL_PERMISSIONS],
  contributor: [
    'expenses.view', 'expenses.add', 'expenses.scan',
    'goals.view', 'goals.contribute',
    'budget.view', 'insights.view', 'income.view',
  ],
  scanner: ['expenses.scan'],
  viewer: ['expenses.view', 'budget.view', 'goals.view', 'insights.view', 'income.view'],
};

export interface FamilyCapabilityFlags {
  canViewExpenses: boolean;
  canAddExpense: boolean;
  canScanReceipt: boolean;
  canEditOthersExpenses: boolean;
  canApprovePending: boolean;
  canViewBudget: boolean;
  canEditBudget: boolean;
  canViewGoals: boolean;
  canContributeGoals: boolean;
  canEditGoals: boolean;
  canViewInsights: boolean;
  canViewIncome: boolean;
  canEditIncome: boolean;
  canManageFamily: boolean;
}

const ALL_ON: FamilyCapabilityFlags = {
  canViewExpenses: true, canAddExpense: true, canScanReceipt: true,
  canEditOthersExpenses: true, canApprovePending: true,
  canViewBudget: true, canEditBudget: true,
  canViewGoals: true, canContributeGoals: true, canEditGoals: true,
  canViewInsights: true, canViewIncome: true, canEditIncome: true,
  canManageFamily: true,
};

export function allCapabilitiesOn(): FamilyCapabilityFlags {
  return { ...ALL_ON };
}

/**
 * Resolves capability flags for a family member.
 *
 * Precedence: personal-only account or owner/admin gets everything, otherwise
 * an explicit per-user override wins over the role preset.
 */
export function resolveFamilyCapabilities(input: {
  isMember: boolean;
  role: FamilyRoleName | null;
  isAdmin?: boolean;
  overrides?: Record<string, boolean> | null;
}): FamilyCapabilityFlags {
  const { isMember, role, isAdmin, overrides } = input;

  // Personal-only account: the personal app is unaffected by family gating.
  if (!isMember || !role) return allCapabilitiesOn();
  if (role === 'owner' || isAdmin) return allCapabilitiesOn();

  const preset = new Set<FamilyPermissionKey>(ROLE_PRESETS[role] || []);
  const has = (key: FamilyPermissionKey): boolean => {
    if (overrides && key in overrides) return !!overrides[key];
    return preset.has(key);
  };

  return {
    canViewExpenses: has('expenses.view'),
    canAddExpense: has('expenses.add'),
    canScanReceipt: has('expenses.scan'),
    canEditOthersExpenses: has('expenses.edit_others'),
    canApprovePending: has('expenses.approve_pending'),
    canViewBudget: has('budget.view'),
    canEditBudget: has('budget.edit'),
    canViewGoals: has('goals.view'),
    canContributeGoals: has('goals.contribute'),
    canEditGoals: has('goals.edit'),
    canViewInsights: has('insights.view'),
    canViewIncome: has('income.view'),
    canEditIncome: has('income.edit'),
    canManageFamily: has('family.manage'),
  };
}
